// Row 4.7 (SHELL-02) — closes CFR-SHELL01-11: the inline-lane crash class React error boundaries
// structurally cannot catch (event-handler throws, setTimeout throws, rejected promises never
// caught — SubAppBoundary.test.tsx's own "HONEST LIMIT" test proves these escape the boundary as a
// window `error`/`unhandledrejection` event, not via React). This is a LAST-RESORT, app-wide signal
// ("something broke somewhere") — it does not replace or attempt to replicate SubAppBoundary's
// per-app contained fallback, and it does not attempt recovery, only visibility. Does not call
// preventDefault(): the browser's own console logging of the underlying error stays intact for
// debugging: this hook only ALSO surfaces it to the shell banner.
//
// Iframe-lane crash detection (CFR-SHELL01-08 — load/handshake timeout across the document
// boundary) is a different mechanism entirely and stays its own open CFR, blocked on the
// (NOT-BUILT) cat-24 iframe-bridge.
import { useEffect, useState } from "react";

export interface GlobalCrash {
  message: string;
}

export interface GlobalCrashSignal {
  crash: GlobalCrash | null;
  dismiss: () => void;
}

export function useGlobalCrash(): GlobalCrashSignal {
  const [crash, setCrash] = useState<GlobalCrash | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onError = (e: ErrorEvent) => {
      setCrash({ message: e.message || "Unknown error" });
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const reason: unknown = e.reason;
      setCrash({ message: reason instanceof Error ? reason.message : String(reason) });
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return { crash, dismiss: () => setCrash(null) };
}
